import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuoteLeft, faChevronLeft, faChevronRight, faStar } from '@fortawesome/free-solid-svg-icons';

const testimonials = [
  {
    id: 1,
    name: 'Laurent Petit',
    image: 'https://randomuser.me/api/portraits/men/42.jpg',
    content: 'GCM FRANCE a refait entièrement la toiture de notre maison avec un professionnalisme exemplaire. Leur équipe a été très attentive à nos besoins et a réalisé un travail de qualité, même dans les détails de finition. Je recommande vivement leurs services de couverture !',
    rating: 5
  },
  {
    id: 2,
    name: 'Céline Durand',
    image: 'https://randomuser.me/api/portraits/women/33.jpg',
    content: 'Suite à une fuite dans notre toiture, nous avons contacté GCM FRANCE en urgence. Leur réactivité a été impressionnante et la réparation parfaitement réalisée. Leur expertise en matière d\'étanchéité nous a évité des dégâts plus importants. Un grand merci !',
    rating: 5
  },
  {
    id: 3,
    name: 'Pierre Legrand',
    image: 'https://randomuser.me/api/portraits/men/67.jpg',
    content: 'J\'ai fait appel à GCM FRANCE pour l\'installation de nouvelles gouttières et la rénovation des chéneaux de ma maison ancienne. Le travail a été réalisé avec soin et dans le respect du cachet de la bâtisse. Un savoir-faire remarquable en couverture traditionnelle.',
    rating: 4
  },
  {
    id: 4,
    name: 'Nathalie Blanc',
    image: 'https://randomuser.me/api/portraits/women/76.jpg',
    content: 'GCM FRANCE a installé des velux sur notre toit et a réalisé l\'isolation de nos combles. La qualité du travail est irréprochable et nous constatons déjà une amélioration significative du confort thermique. Une entreprise de couverture sérieuse et compétente.',
    rating: 5
  }
];

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (autoplay) {
      interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      }, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoplay]);

  const handlePrev = () => {
    setAutoplay(false);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setAutoplay(false);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const renderStars = (rating: number) => {
    return Array(5).fill(0).map((_, i) => (
      <FontAwesomeIcon 
        key={i} 
        icon={faStar} 
        className={i < rating ? "text-yellow-400" : "text-gray-300"} 
      />
    ));
  };

  return (
    <section className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Ce que disent nos clients
          </h2>
          <p className="font-heading text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Découvrez les témoignages de nos clients satisfaits qui nous ont fait confiance pour leurs projets.
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          {/* Testimonial Slider */}
          <div className="overflow-hidden">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="bg-white dark:bg-gray-900 rounded-xl shadow-xl p-8 md:p-12"
            >
              <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img 
                      src={testimonials[currentIndex].image} 
                      alt={testimonials[currentIndex].name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-primary-light"
                    />
                    <div className="absolute -top-2 -left-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faQuoteLeft} className="text-white text-sm" />
                    </div>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="mb-4 flex">
                    {renderStars(testimonials[currentIndex].rating)}
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 text-lg italic mb-6">
                    "{testimonials[currentIndex].content}"
                  </p>
                  
                  <div>
                    <h4 className="font-heading text-xl font-semibold text-gray-900 dark:text-white">
                      {testimonials[currentIndex].name}
                    </h4>

                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center mt-8 space-x-4">
            <button 
              onClick={handlePrev}
              className="w-12 h-12 rounded-full bg-white dark:bg-gray-700 shadow-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              aria-label="Témoignage précédent"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-gray-700 dark:text-gray-200" />
            </button>
            
            <button 
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-white dark:bg-gray-700 shadow-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              aria-label="Témoignage suivant"
            >
              <FontAwesomeIcon icon={faChevronRight} className="text-gray-700 dark:text-gray-200" />
            </button>
          </div>
          
          {/* Indicators */}
          <div className="flex justify-center mt-6 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setAutoplay(false);
                  setCurrentIndex(index);
                }}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentIndex 
                    ? 'bg-primary' 
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                }`}
                aria-label={`Aller au témoignage ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
